// CDSCORegistration.jsx
import React from "react";

export default function CDSCORegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            CDSCO Registration
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            CDSCO Registration for Medical Devices
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks provides expert assistance for CDSCO Registration
            in compliance with medical device and pharmaceutical regulations through
            the SUGAM online portal for import, manufacturing, and market authorization.
          </p>
          <p className="text-gray-700">
            From device classification (Class A/B/C/D) and Form MD application
            preparation through Central/State Licensing Authority approval, the
            process ensures legal market access across India's healthcare ecosystem.
          </p>
        </div>

        {/* Right: Medical device manufacturing image */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/4041391/pexels-photo-4041391.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Medical device manufacturing for CDSCO compliance"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks offers end-to-end CDSCO registration support for
              medical devices across all risk classes through the online SUGAM portal.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Medical device classification (A/B/C/D) as per Medical Device Rules
                  2017 with risk-based licensing pathway determination.
                </li>
                <li>
                  Complete Form MD application preparation (MD-3/7/14/26) with fee
                  payment and document structuring for CLA/SLA submission.
                </li>
                <li>
                  Coordination with CDSCO Zonal/State Offices for queries, inspections,
                  and license issuance (MD-5/9/15/27 permissions).
                </li>
                <li>
                  Post-registration support including label compliance, import license
                  renewal, and pharmacovigilance implementation.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for CDSCO Registration
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling the complete technical
              dossier required for CDSCO medical device registration approval.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Business registration certificate (Incorporation, Partnership Deed, GST).</li>
                <li>
                  Product details including Intended Use, Specifications, Variants,
                  Intended Performers, and Mode of Action documentation.
                </li>
                <li>
                  Manufacturing license (Form 28D/32D) or Import License application
                  with Free Sale Certificate, CPP, or ISO 13485 from country of origin.
                </li>
                <li>
                  Device Master File with risk management, clinical evaluation,
                  labelling compliance, and post-market surveillance plan.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm delivers specialized regulatory expertise for CDSCO medical
              device registration across all device classes and licensing pathways.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Health Products Regulatory Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Complete mastery of Medical Device Rules 2017, CDSCO SUGAM portal,
                  risk classification, and CLA/SLA licensing procedures.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Documentation Handling
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional technical dossier preparation meeting Form MD
                  requirements with 100% SUGAM portal compliance and first-time approvals.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  End-to-End Professional Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Complete lifecycle management from classification through license
                  issuance, import clearance, and ongoing pharmacovigilance compliance.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Access Indian Healthcare Market
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to complete your CDSCO Registration
                and legally manufacture, import, and distribute medical devices across
                India's hospitals, clinics, and healthcare ecosystem.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
